import { formatBytes } from "@prismicio/editor-support/String"
import clsx from "clsx"
import type { KeyboardEvent, MouseEvent } from "react"

import audioPlaceholder from "../../images/audioPlaceholder.svg"
import filePlaceholder from "../../images/filePlaceholder.svg"
import videoPlaceholder from "../../images/videoPlaceholder.svg"
import { Badge } from "../Badge"
import { Box } from "../Box"
import { Checkbox } from "../Checkbox"
import { CheckerBoard } from "../CheckerBoard"
import { ContentEditable } from "../ContentEditable"
import { Image } from "../Image"
import {
  OverflowContainer,
  OverflowEndAdornment,
  OverflowItem
} from "../OverflowContainer/OverflowContainer"
import { ProgressBar, type ProgressBarProps } from "../ProgressBar"
import { Text } from "../Text"
import { Tooltip } from "../Tooltip"
import styles from "./MediaCard.module.css"

const previewHeight = 186

interface Tag {
  id: string
  name: string
}

interface MediaCardProps {
  "aria-label"?: string
  url: string
  filename: string
  filenameHighlight?: string
  alt?: string
  /**
   * The file extension of the media
   * @example "png"
   */
  extension?: string
  kind?: string
  tags?: Tag[]
  onAddTag?: () => void
  width?: number
  height?: number
  selected?: boolean
  loading?: boolean
  /**
   * The upload status of the media
   * @default idle
   */
  status?: MediaProgressStatus
  /**
   * The size of the media in bytes
   */
  size?: number
  /**
   * Function called when the card is clicked
   * @description This only affects the card, not the checkbox
   */
  onCardClick?: (event: MouseEvent | KeyboardEvent) => void
  /**
   * Function called when the checkbox is clicked
   * @description This only affects the checkbox, not the card
   * @param checked - The new checked state of the checkbox
   * @param event - The Mouse / Keyboard event
   */
  onCheckedChange?: (
    checked: boolean,
    event: MouseEvent | KeyboardEvent
  ) => void
  /**
   * Function called when Add a tag is clicked
   * @description This only affects the Add a tag button, not the card
   */
  onAddTagClick?: () => void
}

export function MediaCard(props: MediaCardProps) {
  const {
    ["aria-label"]: ariaLabel,
    url,
    filename,
    filenameHighlight,
    selected = false,
    loading = false,
    status = "idle",
    extension,
    tags,
    onAddTag,
    width,
    height,
    size,
    kind,
    alt,
    onCheckedChange,
    onCardClick,
    onAddTagClick
  } = props

  function getMetadata() {
    if (kind !== "image" && size) {
      return formatBytes(size)
    }

    if (width && height) {
      return `${width}×${height}`
    }

    return ""
  }

  function onClick(event: MouseEvent | KeyboardEvent) {
    if (loading) return
    onCardClick?.(event)
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.code === "Enter" || event.code === "Space") {
      event.preventDefault() // Prevents scroll using Space
      onClick(event)
    }
  }

  const cardTitle = filenameHighlight ?? filename

  const {
    value,
    color,
    height: progressBarHeight,
    transition
  } = getProgressPropsFromMediaStatus(status)

  return (
    <Box aria-label={ariaLabel}>
      <div
        role="button"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onClick={onClick}
        className={clsx(styles.root, selected && styles.selected)}
      >
        <Box flexDirection="column" width="100%">
          <Preview
            extension={extension}
            url={url}
            filename={filename}
            loading={loading}
            kind={kind}
            alt={alt}
          />
        </Box>

        <Box
          flexDirection="column"
          width="100%"
          alignItems="flex-start"
          opacity={loading ? 0.3 : 1}
        >
          <ContentEditable
            content={cardTitle}
            contentVersion={cardTitle}
            decorations={{ em: "highlight", p: "emphasized" }}
            readOnly
          />

          {/* Since some items have no dimensions a fixed height is required in order for the layout to not collapse */}
          <Text variant="small" color="grey11" sx={{ height: 16 }}>
            {getMetadata()}
          </Text>

          <div className={styles.uploadStatusWrapper}>
            <ProgressBar
              value={value}
              color={color}
              height={progressBarHeight}
              transition={transition}
            />
          </div>

          <Box width="100%" alignItems="center" gap={8}>
            <ExtensionBadge extension={extension} />
            {onAddTagClick && !tags?.length && (
              <Badge
                title="Add a tag"
                color="purple"
                size="medium"
                icon="add"
                onClick={(event) => {
                  event?.stopPropagation()
                  if (loading || status === "error" || status === "uploading")
                    return
                  onAddTagClick()
                }}
              />
            )}
            <MediaCardTags tags={tags} onAddTag={onAddTag} />
          </Box>
        </Box>
      </div>
      <MediaCardCheckbox
        checked={selected}
        onCheckedChange={onCheckedChange}
        disabled={loading}
      />
    </Box>
  )
}

interface PreviewProps {
  filename: string
  url: string
  extension?: string
  loading: boolean
  kind?: string
  alt?: string
}

function Preview(props: PreviewProps) {
  const { url, filename, loading, kind, alt } = props
  const placeholder = getPlaceholder(kind)

  const opacity = loading ? 0.3 : 1

  if (placeholder) {
    return (
      <Box
        width="100%"
        height={previewHeight}
        backgroundColor="purple2"
        justifyContent="center"
        alignItems="center"
        opacity={opacity}
      >
        <MediaCardPlaceholder kind={kind} filename={filename} />
      </Box>
    )
  }

  return (
    <CheckerBoard>
      <Box
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        position="relative"
        height={previewHeight}
        width="100%"
        opacity={opacity}
      >
        <Image src={url} alt={alt || filename} sizing="contain" />
      </Box>
    </CheckerBoard>
  )
}

interface MediaCardPlaceholderProps {
  filename: string
  kind?: string
}

export function MediaCardPlaceholder(props: MediaCardPlaceholderProps) {
  const { kind, filename } = props
  const placeholder = getPlaceholder(kind)

  return <img src={placeholder} alt={filename} />
}

/**
 * @private - Get the placeholder for a given kind or MIME type
 *
 * During upload we only have the MIME type (ie. "image/png")
 * After upload we only have the kind (ie. "image")
 *
 * @param kindOrMimeType - the file kind or MIME type
 * @returns - the asset path for the placeholder
 */
function getPlaceholder(kindOrMimeType?: string): string | undefined {
  if (kindOrMimeType?.startsWith("image")) return undefined // MIME type & kind
  if (kindOrMimeType?.startsWith("audio")) return audioPlaceholder // MIME type & kind
  if (kindOrMimeType?.startsWith("video")) return videoPlaceholder // MIME type & kind
  return filePlaceholder // "application", "font", "text" etc. MIME types, "document" kind
}

interface MediaCardTagsProps {
  tags?: Tag[]
  onAddTag?: () => void
}

function MediaCardTags(props: MediaCardTagsProps) {
  const { tags, onAddTag } = props

  function onClick(event?: MouseEvent<HTMLDivElement>) {
    event?.stopPropagation()
    onAddTag?.()
  }

  /**
   * We can only ever fit a max of around 15 tags on the first line
   * of the media card, so in order to prevent the Overflow component
   * from having to handle a possibly very large number of tags.
   * Limit the ones sent to overflow to 15
   */
  const maxNumberOfDisplaytags = 15

  const noTags = !tags || tags.length === 0

  if (!onAddTag && noTags) return null

  if (noTags)
    return <Badge title="Add a tag +" color="purple" onClick={onClick} />

  return (
    <OverflowContainer>
      {tags.slice(0, maxNumberOfDisplaytags).map(({ name, id }) => (
        <OverflowItem key={id}>
          <Badge title={name} color="purple" size="medium" />
        </OverflowItem>
      ))}

      <OverflowEndAdornment
        adornment={({ numberOfVisibleItems }) => (
          <Badge
            title={`+${tags.length - numberOfVisibleItems}`}
            color="purple"
            size="medium"
          />
        )}
      />
    </OverflowContainer>
  )
}

interface ExtensionBadgeProps {
  extension?: string
}
function ExtensionBadge(props: ExtensionBadgeProps) {
  const { extension } = props

  if (!extension) return null
  return <Badge size="medium" title={extension?.toUpperCase() ?? ""} selected />
}

interface MediaCardCheckboxProps {
  checked: boolean
  disabled: boolean
  onCheckedChange?: MediaCardProps["onCheckedChange"]
}

function MediaCardCheckbox(props: MediaCardCheckboxProps) {
  const { checked, disabled, onCheckedChange } = props

  function onClickWrapper(event: MouseEvent | KeyboardEvent) {
    if (disabled) return
    onCheckedChange?.(!checked, event)
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.code === "Enter" || event.code === "Space") {
      event.preventDefault() // Prevents scroll using Space
      onClickWrapper(event)
    }
  }

  return (
    <Tooltip content={checked ? "Unselect" : "Select"} delayDuration={500}>
      <div
        role="presentation"
        className={styles.checkboxWrapper}
        onClick={onClickWrapper}
        onKeyDown={onKeyDown}
      >
        <Checkbox
          onCheckedChange={onCheckedChange}
          checked={checked}
          onClick={(event) => event.stopPropagation()}
          disabled={disabled}
        />
      </div>
    </Tooltip>
  )
}

type MediaProgressStatus = "idle" | "uploading" | "success" | "error"

/**
 * Matches Media Loading status to ProgressBar props
 */
export function getProgressPropsFromMediaStatus(
  status: MediaProgressStatus
): ProgressBarProps {
  switch (status) {
    case "idle":
      return {
        value: 0,
        height: "collapsed",
        color: "grey4"
      }
    case "uploading":
      return {
        value: 90,
        transition: "slow",
        color: "purple9"
      }
    case "success":
      return {
        value: 100,
        height: "collapsed",
        color: "purple9"
      }
    case "error":
      return {
        value: 100,
        color: "tomato11"
      }
  }
}
